import { useMemo, Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../AuthProvider";

// MUI & RTL
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

// Routes & Components
import { publicRoutesList } from './PublicRoutes';
import ProtectedLayout from "./components/ProtectedLayout";
import LazyBoundarySpinner from './components/LazyBoundarySpinner';
import { CssBaseline } from "@mui/material";
import rtlPlugin from '@mui/stylis-plugin-rtl';

import { useAllowedRoutes } from "./routesConfig";
import { RefreshProvider } from "../contexts/RefreshContext";

const NotFound = lazy(() => import('./components/NotFound'));

function Routing() {
    const { lang, user, loading, theme: mode } = useAuth();

    const allowedRoutes = useAllowedRoutes();

    const cache = useMemo(() => createCache({
        key: lang === 'ar' ? 'muirtl' : 'muiltr',
        stylisPlugins: lang === 'ar' ? [rtlPlugin] : [],
    }), [lang]);

    const theme = useMemo(() => createTheme({
        direction: lang === 'ar' ? 'rtl' : 'ltr',
        typography: { fontFamily: 'Almarai, sans-serif' },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    ':root': {
                        '--sidebar-width': '18.5rem',
                        '--main-container-padding': '0.875rem'
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: () => {
                        const isDark = mode === 'dark-theme';
                        return {
                            backgroundColor: isDark ? '#183452' : '#f8fafc',
                            color: isDark ? '#DADCE0' : '#717182',
                            height: '2.8rem',
                            borderRadius: '0.5rem',
                            transition: 'background-color 200ms, border-color 200ms',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#829AB1',
                                borderWidth: '1px',
                            },
                            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDark ? '#4A5568' : '#CBD5E1',
                                borderWidth: '1px',
                            },
                            '& input::placeholder': {
                                color: isDark ? '#DADCE0' : '#717182',
                                opacity: 1,
                            },
                            '&:hover': {
                                backgroundColor: isDark ? '#334155' : '#f1f5f9',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDark ? '#A0AEC0' : '#4A5568',
                                }
                            },
                            '&.Mui-focused': {
                                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                            },
                        };
                    },
                    input: () => {
                        const isDark = mode === 'dark-theme';
                        return {
                            color: isDark ? '#FFFFFF' : '#0F172A',
                            '&.Mui-disabled': {
                                color: isDark ? '#6B7280' : '#9CA3AF',
                                WebkitTextFillColor: isDark ? '#6B7280' : '#9CA3AF',
                            },
                        };
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: () => {
                        const isDark = mode === 'dark-theme';
                        return {
                            color: isDark ? '#DADCE0' : '#4A5568',
                            '&.Mui-disabled': {
                                color: isDark ? '#DADCE0' : '#9CA3AF',
                            },
                            '&.Mui-focused': {
                                color: isDark ? '#0284C7' : '#DADCE0',
                            }
                        };
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: () => {
                        const isDark = mode === 'dark-theme';
                        return {
                            color: isDark ? '#FFF' : 'rgba(0,0,0,0.54)',
                            '&:hover': {
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                color: isDark ? '#F8FAFC' : '#0F172A',
                            },
                            '&.Mui-disabled': {
                                color: isDark ? '#334155' : '#CBD5E1',
                            },
                        };
                    },
                },
            },
        },
    }), [lang, mode]);

    const renderPublicRoutes = (routes) => {
        return routes.map((route, index) => {
            const Component = route.element;
            return (
                <Route
                    key={`pub-${index}`}
                    path={route.path}
                    element={
                        <Suspense fallback={<LazyBoundarySpinner />}>
                            <Component {...(route.props || {})} />
                        </Suspense>
                    }
                />
            );
        });
    };
    // const getDefaultDashboardRedirect = () => {
    //     if (allowedRoutes && allowedRoutes.length > 0) {
    //         const hasMainDash = allowedRoutes.some(r => r.path === "/main_dashboard");
    //         if (hasMainDash) return "/main_dashboard";

    //         const hasEndUserDash = allowedRoutes.some(r => r.path === "/enduser_dashboard");
    //         if (hasEndUserDash) return "/enduser_dashboard";

    //         return allowedRoutes[0].path;
    //     }
    //     return "/login"; 
    // };

    if (loading) return <LazyBoundarySpinner />;
    return (
        <CacheProvider value={cache}>
            <ThemeProvider theme={theme}>
                <div dir={lang === 'ar' ? 'rtl' : 'ltr'} style={{ width: '100%' }}>
                    <CssBaseline />
                    <RefreshProvider userId={user?.id}>
                        <Routes>
                            {renderPublicRoutes(publicRoutesList)}

                            {
                                !user?.id ? (
                                    <Route path="*" element={<Navigate to="/login" replace />} />
                                ) :
                                    (
                                        <>
                                            {allowedRoutes.map((route, idx) => {
                                                const Component = route.element;
                                                return (
                                                    <Route
                                                        key={`app-route-${idx}`}
                                                        path={route.path}
                                                        element={
                                                            <ProtectedLayout>
                                                                <Suspense fallback={<LazyBoundarySpinner />}>
                                                                    <Component {...(route.props || {})} />
                                                                </Suspense>
                                                            </ProtectedLayout>
                                                        }
                                                    />
                                                );
                                            })}

                                            <Route
                                                path="*"
                                                element={
                                                    <ProtectedLayout>
                                                        <Suspense fallback={<LazyBoundarySpinner />}>
                                                            <NotFound />
                                                        </Suspense>
                                                    </ProtectedLayout>
                                                }
                                            />
                                        </>
                                    )}
                        </Routes>
                    </RefreshProvider>
                </div>
            </ThemeProvider>
        </CacheProvider>
    );
}

export default Routing;